import type { ContratProADTO } from '@/features/api/extranet/dto/Demande/PROA'
import type { ErreurSummaryConfiguration } from '@/features/application/erreur-summary'
import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import { idccAutoriseTT } from '@/features/domaine/regles-akto/shared'
import ContratStep from '@/features/interface/demande/proa/steps/contrat/ContratStep.vue'
import DocumentsStep from '@/features/interface/demande/proa/steps/documents/DocumentsStep.vue'
import EmployeurStep from '@/features/interface/demande/proa/steps/employeur/EmployeurStep.vue'
import FormationStep from '@/features/interface/demande/proa/steps/formation/FormationStep.vue'
import IntroductionStep from '@/features/interface/demande/proa/steps/introduction/IntroductionStep.vue'
import SalarieStep from '@/features/interface/demande/proa/steps/salarie/SalarieStep.vue'
import SyntheseStep from '@/features/interface/demande/proa/steps/synthese/SyntheseStep.vue'
import { PROA_ACTIONS } from '@/features/interface/demande/proa/useDemandePROAActions'
import { useDemandePROAActions } from '@/features/interface/demande/proa/useDemandePROAActions.ts'
import type { SetupDemande } from '@/features/interface/demande/shared/components/DemandeContent/DemandeContent.types.ts'
import { storeToRefs } from 'pinia'
import { type Component, provide } from 'vue'
import FinancementStep from './steps/financement/FinancementStep.vue'
import { PROA_MODULES_ACTIONS, useModuleActions } from './steps/formation/useModuleActions'
import { useDemandePROAStore } from './useDemandePROAStore'

type TabKey =
  | 'Introduction'
  | 'Employeur'
  | 'Salarié'
  | 'Contrat'
  | 'Formation'
  | 'Financement'
  | 'Synthèse'
  | 'Documents'

const TABS_VUES: Record<TabKey, Component> = {
  Introduction: IntroductionStep,
  Employeur: EmployeurStep,
  Salarié: SalarieStep,
  Contrat: ContratStep,
  Formation: FormationStep,
  Financement: FinancementStep,
  Synthèse: SyntheseStep,
  Documents: DocumentsStep,
} as const

function getNatureFichiers() {
  const store = useDemandePROAStore()
  const { lov } = storeToRefs(store)

  return lov.value.NatureFichier.map((item) => {
    switch (item.code) {
      case 'AttestationFormateurInterne': {
        return {
          code: 'AttestationFormateurInterne',
          intitule: 'Attestation Service Formation interne + CV des formateurs',
        }
      }
      case 'Convention': {
        return {
          code: 'Convention',
          intitule: 'Convention signée des 2 parties',
        }
      }
      default: {
        return item
      }
    }
  })
}

const TABS_ERR_CONFIG: ErreurSummaryConfiguration<ContratProADTO> = {
  Introduction: { include: (state) => [state.validationNotice] },
  Employeur: {
    includeDeep: (state) => [state.employeur],
  },
  Salarié: {
    includeDeep: (state) => [state.salarie, state.tutorat],
  },
  Contrat: {
    includeDeep: (state) => [state.contrat],
    include: (state) => [state.dispositif],
  },
  Formation: { includeDeep: (state) => [state.formation] },
  Financement: { includeDeep: (state) => [state.financement] },
  Synthèse: {
    include: (state) => [
      state.lieuSignature,
      state.commentaire,
      state.acceptationCg,
      state.attestationSurHonneur,
      state.dateSignature,
    ],
  },
  Documents: {
    includeDeep: (state) => [state.fichiers],
    include: (state) => [state.attestationPiecesJustificatives],
  },
} as const

export const SetupDemandePROA: SetupDemande<'MODEL_PROA', TabKey> = {
  useStore: useDemandePROAStore,
  injectActions: () => {
    const actions = useDemandePROAActions()
    provide(PROA_ACTIONS, actions)
    return actions
  },
  tabFirst: 'Introduction',
  tabsVue: TABS_VUES,
  tabsErrorConfig: (demande) => {
    const store = useDemandePROAStore()
    const { typeContextCourant } = storeToRefs(store)
    if (
      idccAutoriseTT.includes(demande.employeur?.etablissement?.idcc?.code ?? '') &&
      typeContextCourant.value === TypesContextesAutorisesEnum.Adherent
    )
      return TABS_ERR_CONFIG
    const { Financement, ...newTabs } = TABS_ERR_CONFIG
    return newTabs
  },
  getNatureFichiers,
  onInititalize(_actions) {
    provide(PROA_MODULES_ACTIONS, useModuleActions())
  },
} as const
